import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Check, LoaderCircle, Film } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ACTIVE = ['SUBMITTING', 'SUBMITTED', 'PROGRESSING', 'UNKNOWN'];

export const EpisodeHlsControl = ({ episode, light }) => {
  const [state, setState] = useState({ status: episode.transcode_status || 'NONE', error: episode.transcode_error });
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const active = ACTIVE.includes(state.status);
  const ready = state.status === 'COMPLETE';
  const id = episode.id;

  useEffect(() => {
    let stopped = false;
    const refresh = async () => {
      try {
        const { data } = await axios.get(`${API}/admin/episodes/${id}/transcode`, { withCredentials: true });
        if (!stopped) setState(data);
      } catch { /* Existing state remains visible if the network is temporarily unavailable. */ }
    };
    refresh();
    const timer = active ? setInterval(refresh, 8000) : null;
    return () => { stopped = true; clearInterval(timer); };
  }, [id, active]);

  const start = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/admin/episodes/${id}/transcode`, {}, { withCredentials: true });
      setState(data);
      setConfirm(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Impossible de lancer l’encodage.');
    } finally { setBusy(false); }
  };

  return (
    <div className="flex flex-wrap items-center gap-2 min-w-0" data-testid={`ep-hls-${id}`}>
      <span className={`text-[10px] ${ready ? 'text-green-500' : active ? 'text-amber-500' : light ? 'text-gray-500' : 'text-white/50'}`}
        data-testid={`ep-hls-status-${id}`}>
        {ready ? 'HLS prêt' : active ? `Encodage ${state.progress || 0} %` : 'HLS non préparé'}
      </span>
      {ready ? <Check size={12} aria-hidden="true" /> : (
        <button type="button" onClick={() => setConfirm(true)} disabled={busy || active || !episode.video_url}
          data-testid={`ep-hls-prepare-${id}`} title="Préparer cet épisode pour le streaming"
          className="inline-flex items-center gap-1 text-[10px] text-[#E9641A] hover:text-[#FE975E] disabled:opacity-50 transition-colors">
          {active ? <LoaderCircle size={12} className="animate-spin" /> : <Film size={12} />}
          {active ? 'En cours' : 'Préparer HLS'}
        </button>
      )}
      {state.error && <span role="alert" className="text-[10px] text-red-500 break-words" data-testid={`ep-hls-error-${id}`}>{state.error}</span>}
      <Dialog open={confirm} onOpenChange={value => { if (!busy) setConfirm(value); }}>
        <DialogContent data-testid={`ep-hls-confirm-${id}`} className="w-[90vw] max-w-md">
          <DialogHeader>
            <DialogTitle data-testid={`ep-hls-confirm-title-${id}`}>Préparer cet épisode en HLS ?</DialogTitle>
            <DialogDescription data-testid={`ep-hls-confirm-description-${id}`}>
              L’encodage de « {episode.title || 'cet épisode'} » génère des frais AWS. Le fichier original sera conservé. Aucun autre épisode ne sera traité.
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-wrap justify-end gap-2">
            <Button variant="outline" disabled={busy} onClick={() => setConfirm(false)} data-testid={`ep-hls-cancel-${id}`}>Annuler</Button>
            <Button disabled={busy} onClick={start} data-testid={`ep-hls-start-${id}`}>
              {busy ? 'Lancement…' : 'Lancer l’encodage'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};